import { useRef, useState } from 'react';
import { Camera, Crop, FileWarning, Images, ImagePlus, Paperclip } from 'lucide-react';
import { MAX_UPLOAD_FILES, useMediaUpload } from '@/lib/use-media-upload';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PhotoScanner } from '@/components/media/photo-scanner';
import { DuplicateReviewDialog } from '@/components/media/duplicate-review-dialog';
import { NameFacesDialog, type NameFacesItem } from '@/components/media/name-faces-dialog';
import { cn } from '@/lib/utils';

/**
 * Der eine Weg, ein Bild ins Archiv zu legen – für Telefon und Schreibtisch
 * derselbe.
 *
 * Zuerst die Frage „woher kommt das Bild?", dann alles Weitere. Das ist keine
 * Unentschlossenheit, sondern der Unterschied zwischen drei wirklich
 * verschiedenen Absichten:
 *
 *   - ein altes Bild liegt *vor* einem auf dem Tisch → abfotografieren, und
 *     der Zuschnitt rückt Tischkante und Albumrand weg;
 *   - die Bilder liegen schon im Gerät → direkt hochladen, ohne Zwischenschritt;
 *   - ein Bild liegt im Gerät, hat aber noch Ränder → durch denselben
 *     Zuschnitt-Schritt wie eine frische Aufnahme, nur ohne Kamera davor.
 *
 * Wer hier für den Benutzer raten würde, schickt ihn in der Hälfte der Fälle in
 * die falsche Systemansicht – und das kostet mehr als der eine zusätzliche
 * Tipp.
 *
 * Auf dem Schreibtisch war das früher eine Reihe aus drei Knöpfen in der
 * Kopfzeile. Ein Knopf plus Auswahl ist besser, weil die Auswahl *erklären*
 * kann, was der Unterschied ist – in einer Kopfzeile ist für „Mehrere Bilder,
 * ohne Zwischenschritt" kein Platz. Und es ist derselbe Ablauf wie am Telefon,
 * also eine Sache statt zwei.
 *
 * Der Dialog braucht dafür nichts Eigenes: `DialogContent` ist auf schmalen
 * Bildschirmen ohnehin ein Blatt von unten und ab `sm` ein zentriertes Fenster.
 */
export function AddMediaFlow({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const cropRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [scanFile, setScanFile] = useState<File | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  /** Aus der Kamera heraus? Dann führt „Nächstes Foto" wieder zur Kamera. */
  const [fromCamera, setFromCamera] = useState(true);
  const { send, problems, pending, clearPending } = useMediaUpload();
  /**
   * Was gerade angekommen ist und noch benannt werden kann.
   *
   * Aus der Galerie kommen mehrere Bilder ohne Zuschnitt-Schritt – und ohne
   * diesen Stapel endete der Weg dort bei „hochgeladen", während derselbe
   * Vorgang über die Kamera direkt ins Benennen führte.
   */
  const [toName, setToName] = useState<NameFacesItem[]>([]);

  /**
   * Ein aufgenommenes Bild geht durch den Zuschnitt-Schritt. `value` wird
   * geleert, damit dasselbe Foto ein zweites Mal ausgelöst werden kann – ohne
   * das feuert `change` nicht mehr.
   */
  const startScan = (event: React.ChangeEvent<HTMLInputElement>, camera: boolean) => {
    const picked = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!picked) return;
    onOpenChange(false);
    setFromCamera(camera);
    setScanFile(picked);
    setScanOpen(true);
  };

  /**
   * Aus der Galerie geht es ohne Zuschnitt ins Archiv – und direkt weiter zum
   * Benennen.
   *
   * Zugeordnet wird über den Dateinamen: die Antwort sagt, welche Dateien neu
   * angelegt wurden, und nur für die gibt es etwas zu benennen. Doppelte und
   * abgelehnte fallen dabei von selbst heraus.
   */
  const sendPicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    onOpenChange(false);
    const outcome = await send(files);

    const named = outcome.created
      .map((media) => {
        const file = files.find((candidate) => candidate.name === media.originalFilename);
        return file ? { media, file } : null;
      })
      .filter((entry): entry is NameFacesItem => entry !== null)
      // Nur Bilder: bei einer PDF oder einer Tonaufnahme gibt es kein Gesicht.
      .filter((entry) => entry.media.kind === 'image');

    setToName(named);
  };

  return (
    <>
      {/* Die Eingabefelder liegen *außerhalb* des Dialogs: würde er beim
          Antippen schließen und das Feld dabei aus dem Baum fallen, käme das
          `change`-Ereignis nie an. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(event) => startScan(event, true)}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={(event) => void sendPicked(event)}
      />
      {/* Ohne `capture` und ohne `multiple`: der Zuschnitt arbeitet an genau
          einem Bild, und das kommt hier aus der Galerie bzw. vom Rechner. */}
      <input
        ref={cropRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => startScan(event, false)}
      />
      <input
        ref={fileRef}
        type="file"
        multiple
        className="sr-only"
        onChange={(event) => void sendPicked(event)}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          size="sm"
          title="Bild ins Album legen"
          description="Benennen und zuordnen kannst du danach — hier zählt nur, wo das Bild herkommt."
        >
          <div className="flex flex-col gap-2.5">
            {/* Die erste Absicht dunkel und mit goldenem Kreis: sie ist die
                häufigste, und drei gleich aussehende Flächen sind keine
                Empfehlung. */}
            <SourceOption
              tone="ink"
              icon={<Camera className="size-5" aria-hidden />}
              title="Foto abfotografieren"
              hint="Kamera öffnen, Ausschnitt gerade ziehen"
              onClick={() => cameraRef.current?.click()}
            />
            <SourceOption
              tone="gold"
              icon={<Images className="size-5" aria-hidden />}
              title="Aus der Galerie"
              hint={`Mehrere Bilder, ohne Zwischenschritt · bis ${MAX_UPLOAD_FILES} auf einmal`}
              onClick={() => galleryRef.current?.click()}
            />
            <SourceOption
              tone="green"
              icon={<Crop className="size-5" aria-hidden />}
              title="Bild zuschneiden"
              hint="Ränder weg, geradegerückt"
              onClick={() => cropRef.current?.click()}
            />

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex h-11 items-center justify-center gap-2 rounded-full text-[13.5px] font-medium text-ink-2 transition-colors hover:bg-sunken"
            >
              <Paperclip className="size-4" aria-hidden />
              Andere Datei wählen
            </button>

            {problems.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {problems.map((problem) => (
                  <li
                    key={problem.filename}
                    className="flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-[12.5px] text-warn"
                  >
                    <FileWarning className="mt-px size-3.5 shrink-0" aria-hidden />
                    <span>
                      <span className="font-medium">{problem.filename}</span>: {problem.reason}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {pending.length > 0 ? (
        <DuplicateReviewDialog
          pending={pending}
          onOpenChange={(next) => {
            if (!next) clearPending();
          }}
        />
      ) : null}

      <PhotoScanner
        file={scanFile}
        open={scanOpen}
        onOpenChange={(next) => {
          setScanOpen(next);
          if (!next) setScanFile(null);
        }}
        onUpload={(prepared) => send([prepared])}
        onNext={() => (fromCamera ? cameraRef : cropRef).current?.click()}
      />

      {/* Wartet, bis die Rückfrage nach ähnlichen Bildern beantwortet ist:
          zwei Dialoge übereinander sind einer zu viel. */}
      <NameFacesDialog
        items={toName}
        open={toName.length > 0 && pending.length === 0}
        onOpenChange={(next) => {
          if (!next) setToName([]);
        }}
      />
    </>
  );
}

/** Eine der drei Herkünfte – große Fläche, Symbol im Kreis, ein Satz Erklärung. */
function SourceOption({
  tone,
  icon,
  title,
  hint,
  onClick,
}: {
  tone: 'ink' | 'gold' | 'green';
  icon: React.ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3.5 rounded-[18px] px-3.5 py-3.5 text-left',
        tone === 'ink'
          ? 'bg-panel transition-opacity active:opacity-90'
          : 'bg-elevated shadow-xs transition-colors hover:bg-sunken active:bg-sunken',
      )}
    >
      <span
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-full',
          tone === 'ink' && 'bg-accent text-accent-ink',
          tone === 'gold' && 'bg-accent-soft text-accent-strong',
          tone === 'green' && 'bg-ok-soft text-ok',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span
          className={cn(
            'block text-[15.5px] font-semibold',
            tone === 'ink' ? 'text-panel-ink' : 'text-ink',
          )}
        >
          {title}
        </span>
        <span
          className={cn(
            'block text-[12.5px]',
            tone === 'ink' ? 'text-panel-ink-2' : 'text-ink-3',
          )}
        >
          {hint}
        </span>
      </span>
    </button>
  );
}

/**
 * Der Knopf in der Kopfzeile: „Foto hinzufügen“, und die Auswahl dahinter.
 *
 * Einer statt drei – die Unterscheidung gehört in die Auswahl, wo Platz für
 * einen erklärenden Satz ist, und nicht in eine Reihe aus Knöpfen, die alle
 * nur zwei Wörter tragen können.
 */
export function AddMediaButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Schmal nur das Zeichen – dieselbe Regel wie bei `AddAction`. */}
      <Button
        variant="primary"
        size="chrome"
        aria-label="Foto hinzufügen"
        className={cn('w-11 px-0 sm:w-auto sm:px-4', className)}
        onClick={() => setOpen(true)}
      >
        <ImagePlus aria-hidden />
        <span className="hidden sm:inline">Foto hinzufügen</span>
      </Button>
      <AddMediaFlow open={open} onOpenChange={setOpen} />
    </>
  );
}
